import React, { useState, useEffect, useRef, useCallback } from "react";
import AdminLayout from "../../components/admin/AdminLayout.jsx";
import { apiFetch } from "../../utils/api";
import "./AdminShopCoupons.css";

function fmtDate(dateStr) {
  if (!dateStr) return "No expiry";
  return new Date(dateStr).toLocaleDateString("en-ZA", {
    day: "numeric", month: "short", year: "numeric",
  });
}

function isExpired(dateStr) {
  if (!dateStr) return false;
  return new Date(dateStr) < new Date();
}

function isSoon(dateStr) {
  if (!dateStr) return false;
  const diff = new Date(dateStr) - new Date();
  return diff > 0 && diff < 7 * 24 * 60 * 60 * 1000;
}

function fmtDiscount(type, value) {
  if (type === "percent") return `${Number(value)}% OFF`;
  return `R ${Number(value).toFixed(2)} OFF`;
}

/* ─────────────────────────────────
   Dot menu
   ───────────────────────────────── */
function DotMenu({ onEdit, onToggle, onDelete, isActive }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handler(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="asc-dot-menu" ref={ref}>
      <button
        className="asc-dot-menu__trigger"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        title="Actions"
      >
        <span /><span /><span />
      </button>
      {open && (
        <div className="asc-dot-menu__dropdown">
          <button onClick={() => { onEdit(); setOpen(false); }}>
            <span className="asc-dot-menu__icon">✏</span> Edit Coupon
          </button>
          <button onClick={() => { onToggle(); setOpen(false); }}>
            <span className="asc-dot-menu__icon">{isActive ? "🚫" : "✅"}</span>
            {isActive ? "Deactivate" : "Activate"}
          </button>
          <button
            className="asc-dot-menu__danger"
            onClick={() => { onDelete(); setOpen(false); }}
          >
            <span className="asc-dot-menu__icon">🗑</span> Delete Coupon
          </button>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────
   CategoryPicker
   ───────────────────────────────── */
function CategoryPicker({ categories, selected, onChange }) {
  const allSelected = selected.length === 0;

  const toggle = (id) => {
    if (selected.includes(id)) {
      onChange(selected.filter(s => s !== id));
    } else {
      onChange([...selected, id]);
    }
  };

  return (
    <div className="asc-cat-picker">
      {/* All products row */}
      <label className="asc-cat-picker__item asc-cat-picker__item--all">
        <input
          type="checkbox"
          checked={allSelected}
          onChange={() => onChange([])}
        />
        <span className="asc-cat-picker__name">All products</span>
        <span className="asc-cat-picker__hint">No category restriction</span>
      </label>

      <div className="asc-cat-picker__divider" />

      {/* Individual categories — 2 per row */}
      <div className="asc-cat-picker__grid">
        {categories.map(cat => (
          <label key={cat.id} className="asc-cat-picker__item">
            <input
              type="checkbox"
              checked={selected.includes(cat.id)}
              onChange={() => toggle(cat.id)}
            />
            <span className="asc-cat-picker__name">{cat.name}</span>
            {cat.product_count !== undefined && (
              <span className="asc-cat-picker__count">{cat.product_count}</span>
            )}
          </label>
        ))}
      </div>

      {categories.length === 0 && (
        <p className="asc-cat-picker__empty">No categories found.</p>
      )}
    </div>
  );
}
/* ═══════════════════════════════════
   COUPON FORM MODAL  (Add / Edit)
   ═══════════════════════════════════ */
const EMPTY_FORM = {
  code:            "",
  description:     "",
  discount_type:   "percent",
  discount_value:  "",
  min_order_value: "",
  max_uses:        "",
  expires_at:      "",
  active:          true,
  category_ids:    [],   // empty = all products
};

function CouponFormModal({ coupon, onClose, onSaved }) {
  const isEdit = Boolean(coupon);

  const [form,       setForm]       = useState(() => {
    if (!coupon) return EMPTY_FORM;
    return {
      code:            coupon.code            || "",
      description:     coupon.description     || "",
      discount_type:   coupon.discount_type   || "percent",
      discount_value:  coupon.discount_value  ?? "",
      min_order_value: coupon.min_order_value ?? "",
      max_uses:        coupon.max_uses        ?? "",
      expires_at:      coupon.expires_at
        ? new Date(coupon.expires_at).toISOString().slice(0, 16)
        : "",
      active:       coupon.active === 1 || coupon.active === true,
      category_ids: (coupon.categories || []).map(c => c.id),
    };
  });

  const [categories, setCategories] = useState([]);
  const [catLoading, setCatLoading] = useState(true);
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState("");

  /* Load categories for the picker */
  useEffect(() => {
    apiFetch("/api/admin/shop/categories")
      .then(data => setCategories(Array.isArray(data) ? data : []))
      .catch(() => setCategories([]))
      .finally(() => setCatLoading(false));
  }, []);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSave = async () => {
    if (!form.code.trim())
      return setError("Coupon code is required.");
    if (!form.discount_value || isNaN(Number(form.discount_value)) || Number(form.discount_value) <= 0)
      return setError("A valid discount value is required.");
    if (form.discount_type === "percent" && Number(form.discount_value) > 100)
      return setError("Percentage discount cannot exceed 100.");

    setSaving(true);
    setError("");

    const payload = {
      code:            form.code.trim().toUpperCase(),
      description:     form.description.trim() || null,
      discount_type:   form.discount_type,
      discount_value:  parseFloat(form.discount_value),
      min_order_value: form.min_order_value ? parseFloat(form.min_order_value) : 0,
      max_uses:        form.max_uses        ? parseInt(form.max_uses)          : null,
      expires_at:      form.expires_at      || null,
      active:          form.active ? 1 : 0,
      category_ids:    form.category_ids,
    };

    try {
      if (isEdit) {
        await apiFetch(`/api/admin/coupons/${coupon.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
      } else {
        await apiFetch("/api/admin/coupons", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
      }
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="asc-modal-overlay" onClick={onClose}>
      <div className="asc-modal asc-modal--wide" onClick={(e) => e.stopPropagation()}>
        <div className="asc-modal__header">
          <div className="asc-modal__title-row">
            <span className="asc-modal__tag">{isEdit ? "EDIT" : "NEW"}</span>
            <h2 className="asc-modal__title">{isEdit ? "Edit Coupon" : "Add Coupon"}</h2>
          </div>
          <button className="asc-modal__close" onClick={onClose}>✕</button>
        </div>

        <div className="asc-modal__body">
          {error && <div className="asc-modal__error">⚠ {error}</div>}

          {/* Code + Description */}
          <div className="asc-form-row">
            <div className="asc-form-field">
              <label>Coupon Code *</label>
              <input
                type="text"
                placeholder="e.g. SAVE20"
                value={form.code}
                onChange={(e) => set("code", e.target.value.toUpperCase())}
                style={{ textTransform: "uppercase", fontFamily: "monospace", fontWeight: 700 }}
              />
            </div>
            <div className="asc-form-field">
              <label>Description</label>
              <input
                type="text"
                placeholder="Internal note (optional)"
                value={form.description}
                onChange={(e) => set("description", e.target.value)}
              />
            </div>
          </div>

          {/* Discount type + value */}
          <div className="asc-form-row">
            <div className="asc-form-field">
              <label>Discount Type *</label>
              <select
                value={form.discount_type}
                onChange={(e) => set("discount_type", e.target.value)}
              >
                <option value="percent">Percentage (%)</option>
                <option value="fixed">Fixed Amount (R)</option>
              </select>
            </div>
            <div className="asc-form-field">
              <label>
                Discount Value * {form.discount_type === "percent" ? "(%)" : "(R)"}
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                placeholder={form.discount_type === "percent" ? "e.g. 20" : "e.g. 50.00"}
                value={form.discount_value}
                onChange={(e) => set("discount_value", e.target.value)}
              />
            </div>
          </div>

          {/* Min order + max uses */}
          <div className="asc-form-row">
            <div className="asc-form-field">
              <label>Min. Order Value (R)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00 (no minimum)"
                value={form.min_order_value}
                onChange={(e) => set("min_order_value", e.target.value)}
              />
              <span className="asc-form-field__hint">Leave blank for no minimum</span>
            </div>
            <div className="asc-form-field">
              <label>Max Uses</label>
              <input
                type="number"
                min="1"
                step="1"
                placeholder="Unlimited"
                value={form.max_uses}
                onChange={(e) => set("max_uses", e.target.value)}
              />
              <span className="asc-form-field__hint">Leave blank for unlimited</span>
            </div>
          </div>

          {/* Expiry */}
          <div className="asc-form-field">
            <label>Expiry Date &amp; Time</label>
            <input
              type="datetime-local"
              value={form.expires_at}
              onChange={(e) => set("expires_at", e.target.value)}
            />
            <span className="asc-form-field__hint">Leave blank for no expiry</span>
          </div>

          {/* ── Category restriction ── */}
          <div className="asc-form-field asc-form-field--section">
            <label className="asc-form-field__section-label">
              Applies to
              {form.category_ids.length === 0
                ? <span className="asc-scope-badge asc-scope-badge--all">All products</span>
                : <span className="asc-scope-badge asc-scope-badge--limited">
                    {form.category_ids.length} categor{form.category_ids.length === 1 ? "y" : "ies"}
                  </span>
              }
            </label>
            <span className="asc-form-field__hint" style={{ marginBottom: 8 }}>
              Tick specific categories to restrict this coupon. Leave all unchecked to allow
              it on every product.
            </span>
            {catLoading ? (
              <div className="asc-cat-picker__loading">Loading categories…</div>
            ) : (
              <CategoryPicker
                categories={categories}
                selected={form.category_ids}
                onChange={(ids) => set("category_ids", ids)}
              />
            )}
          </div>

          {/* Active toggle */}
          <div className="asc-form-field">
            <div className="asc-form-field__checkbox-row">
              <input
                type="checkbox"
                id="coupon-active"
                checked={form.active}
                onChange={(e) => set("active", e.target.checked)}
              />
              <label htmlFor="coupon-active">Active (coupon is usable by customers)</label>
            </div>
          </div>
        </div>

        <div className="asc-modal__footer">
          <button className="asc-btn asc-btn--cancel" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="asc-btn asc-btn--save" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : isEdit ? "Save Changes" : "Create Coupon"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════
   VIEW MODAL
   ═══════════════════════════════════ */
function ViewModal({ coupon, onClose }) {
  if (!coupon) return null;

  const usagePercent = coupon.max_uses
    ? Math.min(100, Math.round((coupon.used_count / coupon.max_uses) * 100))
    : 0;

  const categories = coupon.categories || [];

  return (
    <div className="asc-modal-overlay" onClick={onClose}>
      <div className="asc-modal" onClick={(e) => e.stopPropagation()}>
        <div className="asc-modal__header">
          <div className="asc-modal__title-row">
            <span className="asc-modal__tag">COUPON</span>
            <h2 className="asc-modal__title" style={{ fontFamily: "monospace" }}>
              {coupon.code}
            </h2>
          </div>
          <button className="asc-modal__close" onClick={onClose}>✕</button>
        </div>

        <div className="asc-modal__body">
          <div>
            <p className="asc-section-title">Coupon Details</p>
            <div className="asc-view-grid">
              {[
                ["Code",          <span style={{ fontFamily:"monospace", fontWeight:700 }}>{coupon.code}</span>],
                ["Description",   coupon.description || "—"],
                ["Discount",      fmtDiscount(coupon.discount_type, coupon.discount_value)],
                ["Type",          coupon.discount_type === "percent" ? "Percentage" : "Fixed Amount"],
                ["Min. Order",    coupon.min_order_value > 0 ? `R ${Number(coupon.min_order_value).toFixed(2)}` : "No minimum"],
                ["Max Uses",      coupon.max_uses ?? "Unlimited"],
                ["Times Used",    coupon.used_count ?? 0],
                ["Expires",       fmtDate(coupon.expires_at)],
                ["Status",        <span className={`asc-active-badge asc-active-badge--${coupon.active ? "yes" : "no"}`}>
                                    {coupon.active ? "✓ Active" : "✕ Inactive"}
                                  </span>],
              ].map(([label, val]) => (
                <div key={label} className="asc-view-row">
                  <span className="asc-view-row__label">{label}</span>
                  <span className="asc-view-row__val">{val}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Scope section */}
          <div>
            <p className="asc-section-title">Applies to</p>
            {categories.length === 0 ? (
              <div className="asc-scope-all">
                <span className="asc-scope-badge asc-scope-badge--all">All products</span>
                <span className="asc-scope-hint">No category restrictions — valid on every product.</span>
              </div>
            ) : (
              <div className="asc-scope-cats">
                {categories.map(c => (
                  <span key={c.id} className="asc-scope-cat-chip">{c.name}</span>
                ))}
              </div>
            )}
          </div>

          {coupon.max_uses && (
            <div>
              <p className="asc-section-title">Usage</p>
              <div style={{ fontSize: ".83rem", color: "#374151", marginBottom: 6 }}>
                {coupon.used_count} / {coupon.max_uses} uses ({usagePercent}%)
              </div>
              <div className="asc-usage__bar-wrap" style={{ width: "100%" }}>
                <div
                  className={`asc-usage__bar ${usagePercent >= 100 ? "asc-usage__bar--full" : ""}`}
                  style={{ width: `${usagePercent}%` }}
                />
              </div>
            </div>
          )}
        </div>

        <div className="asc-modal__footer">
          <button className="asc-btn asc-btn--cancel" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════
   DELETE MODAL
   ═══════════════════════════════════ */
function DeleteModal({ coupon, onClose, onDeleted }) {
  const [deleting, setDeleting] = useState(false);
  const [error,    setError]    = useState("");

  const handleDelete = async () => {
    setDeleting(true);
    setError("");
    try {
      await apiFetch(`/api/admin/coupons/${coupon.id}`, { method: "DELETE" });
      onDeleted();
    } catch (err) {
      setError(err.message);
      setDeleting(false);
    }
  };

  return (
    <div className="asc-modal-overlay" onClick={onClose}>
      <div className="asc-modal" style={{ maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
        <div className="asc-modal__header">
          <div className="asc-modal__title-row">
            <span className="asc-modal__tag">DELETE</span>
            <h2 className="asc-modal__title">Delete Coupon</h2>
          </div>
          <button className="asc-modal__close" onClick={onClose}>✕</button>
        </div>
        <div className="asc-modal__body">
          {error && <div className="asc-modal__error">⚠ {error}</div>}
          <p className="asc-confirm-text">
            Permanently delete coupon{" "}
            <strong style={{ fontFamily: "monospace" }}>{coupon?.code}</strong>?
            <br />This cannot be undone.
          </p>
        </div>
        <div className="asc-modal__footer">
          <button className="asc-btn asc-btn--cancel" onClick={onClose} disabled={deleting}>
            Cancel
          </button>
          <button className="asc-btn asc-btn--danger" onClick={handleDelete} disabled={deleting}>
            {deleting ? "Deleting…" : "🗑 Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════ */
export default function AdminShopCoupons() {
  const [coupons,    setCoupons]    = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, pages: 1 });
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState("");

  const [search,       setSearch]       = useState("");
  const [activeFilter, setActiveFilter] = useState("");
  const [typeFilter,   setTypeFilter]   = useState("");
  const [page,         setPage]         = useState(1);
  const searchTimer = useRef(null);

  const [modal,  setModal]  = useState(null);
  const [target, setTarget] = useState(null);

  const loadCoupons = useCallback(() => {
    setLoading(true);
    setError("");
    const q = new URLSearchParams();
    if (search)       q.set("search", search);
    if (activeFilter) q.set("active", activeFilter);
    if (typeFilter)   q.set("type",   typeFilter);
    q.set("page",  page);
    q.set("limit", "15");

    apiFetch(`/api/admin/coupons?${q}`)
      .then((data) => {
        setCoupons(data.coupons     || []);
        setPagination(data.pagination || { page: 1, total: 0, pages: 1 });
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [search, activeFilter, typeFilter, page]);

  useEffect(() => { loadCoupons(); }, [loadCoupons]);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setPage(1), 400);
  };

  const openModal  = (type, coupon = null) => { setModal(type); setTarget(coupon); };
  const closeModal = () => { setModal(null); setTarget(null); };
  const afterSave  = () => { closeModal(); loadCoupons(); };

  const toggleActive = async (coupon) => {
    try {
      await apiFetch(`/api/admin/coupons/${coupon.id}/toggle`, { method: "PUT" });
      loadCoupons();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <AdminLayout pageTag="SHOP" pageTitle="Coupons">

      {error && <div className="asc-error-bar">⚠ {error}</div>}

      {/* ── Toolbar ── */}
      <div className="asc-toolbar">
        <div className="asc-toolbar__left">
          <div className="asc-search-wrap">
            <span className="asc-search-wrap__icon">🔍</span>
            <input
              className="asc-search"
              type="text"
              placeholder="Search code or description…"
              value={search}
              onChange={handleSearchChange}
            />
            {search && (
              <button
                className="asc-search-wrap__clear"
                onClick={() => { setSearch(""); setPage(1); }}
              >✕</button>
            )}
          </div>

          <select
            className="asc-filter-select"
            value={activeFilter}
            onChange={(e) => { setActiveFilter(e.target.value); setPage(1); }}
          >
            <option value="">All Statuses</option>
            <option value="1">Active</option>
            <option value="0">Inactive</option>
          </select>

          <select
            className="asc-filter-select"
            value={typeFilter}
            onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
          >
            <option value="">All Types</option>
            <option value="percent">Percentage</option>
            <option value="fixed">Fixed Amount</option>
          </select>
        </div>

        <div className="asc-toolbar__right">
          <span className="asc-toolbar__count">
            {loading ? "…" : `${pagination.total} coupon${pagination.total !== 1 ? "s" : ""}`}
          </span>
          <button className="asc-btn-add" onClick={() => openModal("add")}>
            + Add Coupon
          </button>
        </div>
      </div>

      {/* ── Table ── */}
      {loading ? (
        <div className="asc-table-skeleton" />
      ) : coupons.length === 0 ? (
        <div className="asc-empty">
          <span>🎟</span>
          <p>No coupons found.</p>
          {(search || activeFilter || typeFilter) && (
            <button
              className="asc-btn asc-btn--cancel"
              onClick={() => { setSearch(""); setActiveFilter(""); setTypeFilter(""); setPage(1); }}
            >
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <div className="asc-table-wrap">
          <table className="asc-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Discount</th>
                <th>Applies to</th>
                <th>Min. Order</th>
                <th>Usage</th>
                <th>Expires</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {coupons.map((c) => {
                const usagePercent = c.max_uses
                  ? Math.min(100, Math.round((c.used_count / c.max_uses) * 100))
                  : 0;
                const cats = c.categories || [];

                return (
                  <tr key={c.id}>
                    <td>
                      <span className="asc-code-cell__code">{c.code}</span>
                      {c.description && (
                        <span className="asc-code-cell__desc">{c.description}</span>
                      )}
                    </td>
                    <td>
                      <span className="asc-discount">{fmtDiscount(c.discount_type, c.discount_value)}</span>
                    </td>

                    {/* ── Applies-to column ── */}
                    <td>
                      {cats.length === 0 ? (
                        <span className="asc-scope-badge asc-scope-badge--all">All products</span>
                      ) : (
                        <div className="asc-scope-cats asc-scope-cats--inline">
                          {cats.slice(0, 2).map(cat => (
                            <span key={cat.id} className="asc-scope-cat-chip">{cat.name}</span>
                          ))}
                          {cats.length > 2 && (
                            <span className="asc-scope-cat-more">+{cats.length - 2} more</span>
                          )}
                        </div>
                      )}
                    </td>

                    <td style={{ fontSize: ".83rem", color: "#6b7280" }}>
                      {c.min_order_value > 0 ? `R ${Number(c.min_order_value).toFixed(2)}` : "—"}
                    </td>
                    <td>
                      <div className="asc-usage">
                        {c.max_uses
                          ? `${c.used_count} / ${c.max_uses}`
                          : `${c.used_count} used`}
                        {c.max_uses && (
                          <div className="asc-usage__bar-wrap">
                            <div
                              className={`asc-usage__bar ${usagePercent >= 100 ? "asc-usage__bar--full" : ""}`}
                              style={{ width: `${usagePercent}%` }}
                            />
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <span
                        className={`asc-expires ${
                          isExpired(c.expires_at)
                            ? "asc-expires--expired"
                            : isSoon(c.expires_at)
                            ? "asc-expires--soon"
                            : ""
                        }`}
                      >
                        {isExpired(c.expires_at) && c.expires_at ? "⚠ " : ""}
                        {fmtDate(c.expires_at)}
                      </span>
                    </td>
                    <td>
                      <span className={`asc-active-badge asc-active-badge--${c.active ? "yes" : "no"}`}>
                        {c.active ? "✓ Active" : "✕ Inactive"}
                      </span>
                    </td>
                    <td>
                      <DotMenu
                        onEdit={()    => openModal("edit",   c)}
                        onToggle={()  => toggleActive(c)}
                        onDelete={()  => openModal("delete", c)}
                        isActive={!!c.active}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Pagination ── */}
      {pagination.pages > 1 && (
        <div className="asc-pagination">
          <button
            className="asc-pagination__btn"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >← Prev</button>
          {Array.from({ length: pagination.pages }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              className={`asc-pagination__btn ${p === page ? "active" : ""}`}
              onClick={() => setPage(p)}
            >{p}</button>
          ))}
          <button
            className="asc-pagination__btn"
            disabled={page >= pagination.pages}
            onClick={() => setPage((p) => p + 1)}
          >Next →</button>
        </div>
      )}

      {/* ── Modals ── */}
      {modal === "add"    && <CouponFormModal coupon={null}   onClose={closeModal} onSaved={afterSave} />}
      {modal === "edit"   && <CouponFormModal coupon={target} onClose={closeModal} onSaved={afterSave} />}
      {modal === "view"   && <ViewModal       coupon={target} onClose={closeModal} />}
      {modal === "delete" && <DeleteModal     coupon={target} onClose={closeModal} onDeleted={afterSave} />}
    </AdminLayout>
  );
}